import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import { fmtNum, fmtSGD, fmtDate, fmtDateTime } from "@/lib/format";
import { SETUP, WIZARD, TOUR } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import { GuidedTour } from "@/components/GuidedTour";
import { SETUP_TOURS } from "@/lib/tours";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Pencil, Calculator, Building2, MapPin, Grid3x3, Boxes, FileText, Lock, CheckSquare, AlertTriangle, History, Rocket, HelpCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "entities", label: "Legal Entities", icon: Building2 },
  { key: "sites", label: "Sites", icon: MapPin },
  { key: "zones", label: "Zones", icon: Grid3x3 },
  { key: "assets", label: "Assets", icon: Boxes },
  { key: "contracts", label: "Contracts", icon: FileText },
];

const SINGULAR = { entities: "legal entity", sites: "site", zones: "zone", assets: "asset", contracts: "contract" };
const AUDIT_TYPE = { entities: "entity", sites: "site", zones: "zone", assets: "asset", contracts: "contract" };

export default function SetupPage() {
  const { hasRole } = useAuth();
  const ws = useWorkspace();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const tourReq = sp.get("tour");
  const focusRecord = sp.get("record");
  const canEdit = hasRole("tenant_admin", "contract_manager");
  const [tab, setTab] = useState(() => sp.get("tab") || "entities");
  const [data, setData] = useState({ entities: [], sites: [], zones: [], assets: [], contracts: [] });

  const loadAll = async () => {
    const [e, s, z, a, c] = await Promise.all([
      api.get("/entities"), api.get("/sites"), api.get("/zones"), api.get("/assets"), api.get("/contracts"),
    ]);
    setData({ entities: e.data, sites: s.data, zones: z.data, assets: a.data, contracts: c.data });
  };
  useEffect(() => { loadAll(); }, []);

  return (
    <div className="space-y-5" data-testid={SETUP.root}>
      <div>
        <h1 className="font-heading text-2xl font-bold text-forest-dark">Setup & Master Data</h1>
        <p className="text-sm text-muted-foreground">Define your entities, sites, zones, assets and contracts — the foundation every workspace builds on.</p>
      </div>

      <button onClick={() => navigate("/setup/wizard")} data-testid={WIZARD.openLauncher}
        className="group flex w-full items-center justify-between gap-4 rounded-2xl border border-forest-mid/30 bg-forest-deep p-5 text-left text-emerald-50 transition-shadow hover:shadow-lg">
        <div className="flex items-center gap-4">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sage-accent"><Rocket className="h-6 w-6 text-white" /></div>
          <div>
            <p className="font-heading text-lg font-bold">Start a project</p>
            <p className="text-sm text-emerald-100/80">A guided wizard to stand up a won project or a tender pursuit, step by step — with tours that show exactly what to enter.</p>
          </div>
        </div>
        <ArrowRight className="h-5 w-5 text-emerald-200 transition-transform group-hover:translate-x-1" />
      </button>

      <CompletenessSummary data={data} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={SETUP.tab(t.key)} className="gap-1.5">
              <t.icon className="h-4 w-4" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="entities" className="mt-4">
          <ResourceTable rkey="entities" rows={data.entities} canEdit={canEdit} reload={loadAll} ws={ws} data={data} tourKind="entity" autoTour={tourReq === "entity"} focusId={tab === "entities" ? focusRecord : null}
            columns={[{ k: "name", h: "Name" }, { k: "type", h: "Type", render: (v) => (v || "").replace(/_/g, " ") }, { k: "registration_no", h: "Reg. No." }]} />
        </TabsContent>
        <TabsContent value="sites" className="mt-4">
          <ResourceTable rkey="sites" rows={data.sites} canEdit={canEdit} reload={loadAll} ws={ws} data={data} tourKind="site" autoTour={tourReq === "site"} focusId={tab === "sites" ? focusRecord : null}
            columns={[{ k: "name", h: "Site" }, { k: "site_type", h: "Type", render: (v) => (v || "").replace(/_/g, " ") }, { k: "location", h: "Location" }, { k: "area_sqm", h: "Area (m²)", render: (v) => fmtNum(v) }]} />
        </TabsContent>
        <TabsContent value="zones" className="mt-4">
          <ResourceTable rkey="zones" rows={data.zones} canEdit={canEdit} reload={loadAll} ws={ws} data={data} tourKind="zone" autoTour={tourReq === "zone"} focusId={tab === "zones" ? focusRecord : null}
            columns={[{ k: "name", h: "Zone" }, { k: "site_id", h: "Site", render: (v) => data.sites.find((s) => s.id === v)?.name }, { k: "zone_type", h: "Type", render: (v) => (v || "").replace(/_/g, " ") }, { k: "area_sqm", h: "Area (m²)", render: (v) => fmtNum(v) }]} />
        </TabsContent>
        <TabsContent value="assets" className="mt-4">
          <ResourceTable rkey="assets" rows={data.assets} canEdit={canEdit} reload={loadAll} ws={ws} data={data} tourKind="asset" autoTour={tourReq === "asset"} focusId={tab === "assets" ? focusRecord : null}
            columns={[{ k: "name", h: "Asset" }, { k: "asset_type", h: "Type", render: (v) => (v || "").replace(/_/g, " ") }, { k: "code", h: "Code", mono: true }, { k: "site_id", h: "Site", render: (v) => data.sites.find((s) => s.id === v)?.name }, { k: "condition", h: "Condition" }]} />
        </TabsContent>
        <TabsContent value="contracts" className="mt-4">
          <ResourceTable rkey="contracts" rows={data.contracts} canEdit={canEdit} reload={loadAll} ws={ws} data={data} tourKind="contract" autoTour={tourReq === "contract"} focusId={tab === "contracts" ? focusRecord : null}
            columns={[{ k: "name", h: "Contract" }, { k: "client_owner", h: "Client owner" }, { k: "value_sgd", h: "Value", render: (v) => fmtSGD(v) }, { k: "end_date", h: "Ends", render: (v) => fmtDate(v) }]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function emptyForm(rkey, ws) {
  const base = {
    entities: { name: "", type: "landscape_maintenance", registration_no: "", notes: "" },
    sites: { entity_id: ws.entityId || "", name: "", site_type: "park", area_sqm: "", location: "", notes: "" },
    zones: { site_id: ws.siteId || "", name: "", zone_type: "turf", area_sqm: "", notes: "" },
    assets: { site_id: ws.siteId || "", zone_id: "", name: "", asset_type: "valve", code: "", quantity: "", condition: "good", notes: "" },
    contracts: { entity_id: ws.entityId || "", name: "", scope: "", site_ids: [], client_owner: "", payer: "", reporter: "", start_date: "", end_date: "", value_sgd: "", status: "active" },
  };
  return base[rkey];
}

function ResourceTable({ rkey, rows, columns, canEdit, reload, ws, data, tourKind, autoTour, focusId }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const tourStarted = useRef(false);
  const focusDone = useRef(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const bulkEnabled = canEdit && (rkey === "sites" || rkey === "assets");
  const tour = tourKind ? SETUP_TOURS[tourKind] : null;

  const startTour = () => { setForm(emptyForm(rkey, ws)); setOpen(true); setTimeout(() => setTourOpen(true), 220); };
  useEffect(() => {
    if (autoTour && tour && !tourStarted.current) { tourStarted.current = true; startTour(); }
  }, [autoTour]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setForm(emptyForm(rkey, ws)); setOpen(true); };
  const openEdit = (r) => {
    setForm({ ...r, zone_id: rkey === "assets" ? (r.zone_id || "none") : r.zone_id, site_ids: r.site_ids || [] });
    setOpen(true);
  };

  useEffect(() => {
    if (focusId && !focusDone.current) {
      const row = rows.find((r) => r.id === focusId);
      if (row) { focusDone.current = true; openEdit(row); }
    }
  }, [focusId, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRow = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected((s) => (s.length === rows.length ? [] : rows.map((r) => r.id)));
  const afterBulk = async () => { setSelected([]); await reload(); await ws.reload(); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ["id", "tenant_id", "created_at", "updated_at"].forEach((k) => delete payload[k]);
      ["area_sqm", "quantity", "value_sgd"].forEach((n) => {
        if (payload[n] === "" || payload[n] == null) delete payload[n];
        else payload[n] = Number(payload[n]);
      });
      if (payload.zone_id === "none") payload.zone_id = null;
      if (form.id) await api.patch(`/${rkey}/${form.id}`, payload);
      else await api.post(`/${rkey}`, payload);
      toast.success("Saved");
      setOpen(false);
      await reload();
      await ws.reload();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/${rkey}/${id}`);
      toast.success("Deleted");
      await reload();
      await ws.reload();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const extraCols = (canEdit ? 1 : 0) + (bulkEnabled ? 1 : 0);

  return (
    <Card className="border-sage-light">
      <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <span className="text-sm font-medium text-muted-foreground">{rows.length} record{rows.length === 1 ? "" : "s"}</span>
        <div className="flex items-center gap-2">
          {bulkEnabled && selected.length > 0 && (
            <>
              <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid={SETUP.bulkEditBtn(rkey)}>
                <CheckSquare className="mr-1 h-4 w-4" /> Bulk edit ({selected.length})
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected([])}>Clear</Button>
            </>
          )}
          {canEdit && tour && (
            <Button size="sm" variant="ghost" onClick={startTour} data-testid={TOUR.startBtn(tourKind)} className="text-forest-mid">
              <HelpCircle className="mr-1 h-4 w-4" /> Take a tour
            </Button>
          )}
          {canEdit ? (
            <Button size="sm" onClick={openNew} data-testid={SETUP.addBtn(rkey)} className="bg-forest-mid hover:bg-forest-dark">
              <Plus className="mr-1 h-4 w-4" /> Add
            </Button>
          ) : (
            <span className="flex items-center gap-1 text-xs text-muted-foreground"><Lock className="h-3 w-3" /> Read only</span>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {bulkEnabled && (
                <TableHead className="w-10">
                  <Checkbox checked={rows.length > 0 && selected.length === rows.length}
                    onCheckedChange={toggleAll} data-testid={SETUP.selectAll(rkey)} aria-label="Select all" />
                </TableHead>
              )}
              {columns.map((c) => <TableHead key={c.k}>{c.h}</TableHead>)}
              {canEdit && <TableHead className="w-24 text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={columns.length + extraCols} className="py-8 text-center text-sm text-muted-foreground">No records yet.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={SETUP.row(rkey, r.id)}
                className={canEdit ? "cursor-pointer hover:bg-sage-surface/50" : ""}
                onClick={canEdit ? () => openEdit(r) : undefined}>
                {bulkEnabled && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggleRow(r.id)}
                      data-testid={SETUP.selectRow(rkey, r.id)} aria-label="Select row" />
                  </TableCell>
                )}
                {columns.map((c) => (
                  <TableCell key={c.k} className={c.mono ? "font-mono text-xs" : "text-sm"}>
                    {c.render ? (c.render(r[c.k]) || "—") : (r[c.k] || "—")}
                  </TableCell>
                ))}
                {canEdit && (
                  <TableCell className="whitespace-nowrap text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-forest-mid hover:text-forest-dark" onClick={(e) => { e.stopPropagation(); openEdit(r); }} data-testid={SETUP.editBtn(rkey, r.id)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500 hover:text-rose-700" onClick={(e) => { e.stopPropagation(); del(r.id); }} data-testid={SETUP.deleteBtn(rkey, r.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg" data-testid={SETUP.dialog}
          onInteractOutside={(e) => { if (tourOpen) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (tourOpen) e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle className="font-heading">{form.id ? "Edit" : "New"} {SINGULAR[rkey]}</DialogTitle>
            <DialogDescription>{form.id ? "Update the details below. Calculated figures are read-only." : "Fill in the details to create a new record."}</DialogDescription>
          </DialogHeader>
          <ResourceForm rkey={rkey} form={form} set={set} data={data} />
          <CalcPanel rkey={rkey} form={form} data={data} />
          <RecordAudit rkey={rkey} id={form.id} open={open} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-forest-mid hover:bg-forest-dark" data-testid={SETUP.saveBtn}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {bulkEnabled && (
        <BulkEditDialog rkey={rkey} open={bulkOpen} onOpenChange={setBulkOpen}
          ids={selected} rows={rows} data={data} onDone={afterBulk} />
      )}
      {tour && (
        <GuidedTour open={tourOpen} steps={tour.steps} title={`${tour.name} tour`} onClose={() => setTourOpen(false)} />
      )}
    </Card>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label>{label}</Label>{children}</div>
);

function ResourceForm({ rkey, form, set, data }) {
  const T = (name, label, type = "text") => (
    <F label={label}><Input type={type} value={form[name] ?? ""} onChange={(e) => set(name, e.target.value)} data-testid={SETUP.field(name)} /></F>
  );
  const TA = (name, label) => (
    <F label={label}><Textarea value={form[name] ?? ""} onChange={(e) => set(name, e.target.value)} data-testid={SETUP.field(name)} /></F>
  );
  const S = (name, label, options) => (
    <F label={label}>
      <Select value={form[name] || ""} onValueChange={(v) => set(name, v)}>
        <SelectTrigger data-testid={SETUP.field(name)}><SelectValue placeholder={`Select ${label}`} /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
      </Select>
    </F>
  );

  if (rkey === "entities")
    return <div className="space-y-4">{T("name", "Entity name")}{S("type", "Type", [{ v: "landscape_maintenance", l: "Landscape maintenance" }, { v: "design_build", l: "Design & build" }])}{T("registration_no", "Registration no.")}{TA("notes", "Notes")}</div>;

  if (rkey === "sites")
    return <div className="space-y-4">{S("entity_id", "Legal entity", data.entities.map((e) => ({ v: e.id, l: e.name })))}{T("name", "Site name")}{S("site_type", "Site type", [{ v: "park", l: "Park" }, { v: "commercial_grounds", l: "Commercial grounds" }, { v: "streetscape", l: "Streetscape" }, { v: "residential", l: "Residential" }])}{T("area_sqm", "Area (m²)", "number")}{T("location", "Location")}{TA("notes", "Notes")}</div>;

  if (rkey === "zones")
    return <div className="space-y-4">{S("site_id", "Site", data.sites.map((s) => ({ v: s.id, l: s.name })))}{T("name", "Zone name")}{S("zone_type", "Zone type", [{ v: "turf", l: "Turf" }, { v: "shrub_bed", l: "Shrub bed" }, { v: "rain_garden", l: "Rain garden" }, { v: "tree_belt", l: "Tree belt" }])}{T("area_sqm", "Area (m²)", "number")}</div>;

  if (rkey === "assets")
    return <div className="space-y-4">{S("site_id", "Site", data.sites.map((s) => ({ v: s.id, l: s.name })))}{S("zone_id", "Zone (optional)", [{ v: "none", l: "— none —" }, ...data.zones.filter((z) => z.site_id === form.site_id).map((z) => ({ v: z.id, l: z.name }))])}{T("name", "Asset name")}{S("asset_type", "Type", [{ v: "valve", l: "Valve" }, { v: "meter", l: "Meter" }, { v: "plant_cohort", l: "Plant cohort" }, { v: "equipment", l: "Equipment" }])}{T("code", "Asset code")}{T("quantity", "Quantity", "number")}{S("condition", "Condition", [{ v: "good", l: "Good" }, { v: "fair", l: "Fair" }, { v: "poor", l: "Poor" }])}</div>;

  if (rkey === "contracts")
    return (
      <div className="space-y-4">
        {S("entity_id", "Legal entity", data.entities.map((e) => ({ v: e.id, l: e.name })))}
        {T("name", "Contract name")}
        {TA("scope", "Scope")}
        <F label="Sites covered">
          <div className="space-y-1.5 rounded-lg border p-3">
            {data.sites.filter((s) => s.entity_id === form.entity_id).map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-sm">
                <Checkbox checked={(form.site_ids || []).includes(s.id)}
                  onCheckedChange={(ck) => {
                    const cur = form.site_ids || [];
                    set("site_ids", ck ? [...cur, s.id] : cur.filter((x) => x !== s.id));
                  }} />
                {s.name}
              </label>
            ))}
            {data.sites.filter((s) => s.entity_id === form.entity_id).length === 0 && <p className="text-xs text-muted-foreground">No sites for this entity yet.</p>}
          </div>
        </F>
        {T("client_owner", "Client owner")}
        <div className="grid grid-cols-2 gap-3">{T("payer", "Payer")}{T("reporter", "Reporter")}</div>
        <div className="grid grid-cols-2 gap-3">{T("start_date", "Start date", "date")}{T("end_date", "End date", "date")}</div>
        {T("value_sgd", "Contract value (SGD)", "number")}
      </div>
    );
  return null;
}

function monthsBetween(a, b) {
  const d1 = new Date(a), d2 = new Date(b);
  if (isNaN(d1) || isNaN(d2)) return null;
  return Math.max(0, Math.round((d2 - d1) / (1000 * 60 * 60 * 24 * 30.44)));
}

function calcFields(rkey, form, data) {
  const id = form.id;
  if (rkey === "entities") {
    const sites = data.sites.filter((s) => s.entity_id === id);
    const siteIds = sites.map((s) => s.id);
    const zones = data.zones.filter((z) => siteIds.includes(z.site_id));
    const assets = data.assets.filter((a) => siteIds.includes(a.site_id));
    const contracts = data.contracts.filter((c) => c.entity_id === id);
    const totalVal = contracts.reduce((s, c) => s + (c.value_sgd || 0), 0);
    return [
      { label: "Sites", value: sites.length },
      { label: "Zones", value: zones.length },
      { label: "Assets", value: assets.length },
      { label: "Contracts", value: contracts.length },
      { label: "Total contract value", value: fmtSGD(totalVal) },
    ];
  }
  if (rkey === "sites") {
    const zones = data.zones.filter((z) => z.site_id === id);
    const zoneArea = zones.reduce((s, z) => s + (z.area_sqm || 0), 0);
    const assets = data.assets.filter((a) => a.site_id === id);
    const contracts = data.contracts.filter((c) => (c.site_ids || []).includes(id));
    const area = Number(form.area_sqm) || 0;
    const cov = area ? Math.round((zoneArea / area) * 100) : null;
    return [
      { label: "Zones", value: zones.length },
      { label: "Zoned area", value: fmtNum(zoneArea, " m²") },
      { label: "Zone coverage", value: cov == null ? "—" : `${cov}%` },
      { label: "Assets", value: assets.length },
      { label: "Contracts covering", value: contracts.length },
    ];
  }
  if (rkey === "zones") {
    const site = data.sites.find((s) => s.id === form.site_id);
    const assets = data.assets.filter((a) => a.zone_id === id);
    const area = Number(form.area_sqm) || 0;
    const siteArea = site?.area_sqm || 0;
    const cov = siteArea ? Math.round((area / siteArea) * 100) : null;
    return [
      { label: "Parent site", value: site?.name || "—" },
      { label: "Assets in zone", value: assets.length },
      { label: "Share of site area", value: cov == null ? "—" : `${cov}%` },
    ];
  }
  if (rkey === "assets") {
    const site = data.sites.find((s) => s.id === form.site_id);
    const zone = data.zones.find((z) => z.id === form.zone_id);
    return [
      { label: "Location", value: [site?.name, zone?.name].filter(Boolean).join(" / ") || "—" },
      { label: "Quantity", value: form.quantity ? fmtNum(form.quantity) : "—" },
    ];
  }
  if (rkey === "contracts") {
    const months = form.start_date && form.end_date ? monthsBetween(form.start_date, form.end_date) : null;
    const daysLeft = form.end_date ? Math.ceil((new Date(form.end_date) - new Date()) / 86400000) : null;
    return [
      { label: "Sites covered", value: (form.site_ids || []).length },
      { label: "Term", value: months == null ? "—" : `${months} months` },
      { label: "Days remaining", value: daysLeft == null ? "—" : daysLeft < 0 ? "Ended" : fmtNum(daysLeft) },
      { label: "Value", value: fmtSGD(Number(form.value_sgd) || 0) },
    ];
  }
  return [];
}

function CalcPanel({ rkey, form, data }) {
  const rows = calcFields(rkey, form, data);
  if (!form.id || !rows.length) return null;
  return (
    <div className="rounded-lg border border-sage-light bg-sage-surface p-3" data-testid="setup-calc-panel">
      <p className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-forest-dark">
        <Calculator className="h-3.5 w-3.5" /> Calculated
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
        {rows.map((r) => (
          <div key={r.label}>
            <p className="text-[11px] text-muted-foreground">{r.label}</p>
            <p className="text-sm font-semibold text-forest-dark">{r.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecordAudit({ rkey, id, open }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!open || !id) { setRows(null); return; }
    let active = true;
    setRows(null);
    api.get(`/audit?entity_type=${AUDIT_TYPE[rkey]}&entity_id=${id}`)
      .then((r) => { if (active) setRows(r.data); })
      .catch(() => { if (active) setRows([]); });
    return () => { active = false; };
  }, [rkey, id, open]);

  if (!id) return null;
  return (
    <div className="rounded-lg border" data-testid={SETUP.recordAudit}>
      <p className="flex items-center gap-1 border-b px-3 py-2 text-xs font-semibold uppercase tracking-wide text-forest-dark">
        <History className="h-3.5 w-3.5" /> Recent changes
      </p>
      <div className="max-h-40 overflow-y-auto">
        {rows == null ? (
          <p className="px-3 py-3 text-xs text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="px-3 py-3 text-xs text-muted-foreground">No changes recorded yet.</p>
        ) : (
          rows.map((a) => (
            <div key={a.id} className="flex items-start justify-between gap-3 border-b px-3 py-2 last:border-0">
              <div className="min-w-0">
                <p className="text-xs font-medium capitalize text-forest-dark">
                  {a.action.replace(/_/g, " ")}{a.changes?.name ? ` · ${a.changes.name}` : ""}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {a.user_name} <span className="capitalize">({(a.user_role || "").replace(/_/g, " ")})</span>
                </p>
              </div>
              <span className="whitespace-nowrap text-[11px] text-muted-foreground">{fmtDateTime(a.timestamp)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const BULK_FIELDS = {
  sites: [
    { name: "site_type", label: "Site type", type: "select", options: [{ v: "park", l: "Park" }, { v: "commercial_grounds", l: "Commercial grounds" }, { v: "streetscape", l: "Streetscape" }, { v: "residential", l: "Residential" }] },
    { name: "location", label: "Location", type: "text" },
    { name: "notes", label: "Notes", type: "textarea" },
  ],
  assets: [
    { name: "asset_type", label: "Type", type: "select", options: [{ v: "valve", l: "Valve" }, { v: "meter", l: "Meter" }, { v: "plant_cohort", l: "Plant cohort" }, { v: "equipment", l: "Equipment" }] },
    { name: "condition", label: "Condition", type: "select", options: [{ v: "good", l: "Good" }, { v: "fair", l: "Fair" }, { v: "poor", l: "Poor" }] },
    { name: "site_id", label: "Site", type: "select", dynamic: "sites" },
    { name: "zone_id", label: "Zone", type: "select", dynamic: "zones" },
  ],
};

function BulkEditDialog({ rkey, open, onOpenChange, ids, rows, data, onDone }) {
  const fields = BULK_FIELDS[rkey] || [];
  const [enabled, setEnabled] = useState({});
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setEnabled({}); setVals({}); } }, [open]);

  const options = (f) => {
    if (f.dynamic === "sites") return data.sites.map((s) => ({ v: s.id, l: s.name }));
    if (f.dynamic === "zones") return [{ v: "none", l: "— none —" }, ...data.zones.map((z) => ({ v: z.id, l: `${z.name} (${data.sites.find((s) => s.id === z.site_id)?.name || "?"})` }))];
    return f.options;
  };

  const apply = async () => {
    const changes = {};
    fields.forEach((f) => {
      if (!enabled[f.name]) return;
      changes[f.name] = f.name === "zone_id" && vals[f.name] === "none" ? null : (vals[f.name] ?? "");
    });
    if (Object.keys(changes).length === 0) { toast.error("Tick at least one field to change"); return; }
    setSaving(true);
    try {
      const chosen = rows.filter((r) => ids.includes(r.id));
      for (const r of chosen) {
        const payload = { ...r, ...changes };
        ["id", "tenant_id", "created_at", "updated_at"].forEach((k) => delete payload[k]);
        ["area_sqm", "quantity", "value_sgd"].forEach((n) => {
          if (payload[n] === "" || payload[n] == null) delete payload[n];
          else payload[n] = Number(payload[n]);
        });
        if (payload.zone_id === "none") payload.zone_id = null;
        await api.patch(`/${rkey}/${r.id}`, payload);
      }
      toast.success(`Updated ${chosen.length} record${chosen.length === 1 ? "" : "s"}`);
      onOpenChange(false);
      await onDone();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg" data-testid={SETUP.bulkDialog}>
        <DialogHeader>
          <DialogTitle className="font-heading">Bulk edit {SINGULAR[rkey]}s</DialogTitle>
          <DialogDescription>Tick a field to apply its value to all {ids.length} selected record{ids.length === 1 ? "" : "s"}. Unticked fields stay unchanged.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {fields.map((f) => (
            <div key={f.name} className="rounded-lg border p-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Checkbox checked={!!enabled[f.name]} onCheckedChange={(ck) => setEnabled((e) => ({ ...e, [f.name]: !!ck }))} data-testid={SETUP.bulkFieldToggle(f.name)} />
                {f.label}
              </label>
              {enabled[f.name] && (
                <div className="mt-2">
                  {f.type === "select" ? (
                    <Select value={vals[f.name] || ""} onValueChange={(v) => setVals((s) => ({ ...s, [f.name]: v }))}>
                      <SelectTrigger data-testid={SETUP.bulkField(f.name)}><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                      <SelectContent>{options(f).map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.type === "textarea" ? (
                    <Textarea value={vals[f.name] || ""} onChange={(e) => setVals((s) => ({ ...s, [f.name]: e.target.value }))} data-testid={SETUP.bulkField(f.name)} />
                  ) : (
                    <Input value={vals[f.name] || ""} onChange={(e) => setVals((s) => ({ ...s, [f.name]: e.target.value }))} data-testid={SETUP.bulkField(f.name)} />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={apply} disabled={saving} className="bg-forest-mid hover:bg-forest-dark" data-testid={SETUP.bulkSaveBtn}>Apply to {ids.length}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompletenessSummary({ data }) {
  const now = Date.now();
  const zonedSiteIds = new Set(data.zones.map((z) => z.site_id));
  const entityWithSite = new Set(data.sites.map((s) => s.entity_id));
  const items = [
    { label: "Entities without sites", value: data.entities.filter((e) => !entityWithSite.has(e.id)).length },
    { label: "Sites without zones", value: data.sites.filter((s) => !zonedSiteIds.has(s.id)).length },
    { label: "Assets without a zone", value: data.assets.filter((a) => !a.zone_id).length },
    { label: "Contracts ending ≤90 days", value: data.contracts.filter((c) => { if (!c.end_date) return false; const d = (new Date(c.end_date) - now) / 86400000; return d >= 0 && d <= 90; }).length },
  ];
  return (
    <Card className="overflow-hidden border-sage-light" data-testid={SETUP.completeness}>
      <div className="flex items-center gap-1.5 border-b px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-forest-dark">
        <AlertTriangle className="h-4 w-4 text-amber-500" /> Setup completeness
      </div>
      <div className="grid grid-cols-2 gap-px bg-sage-light sm:grid-cols-4">
        {items.map((it) => (
          <div key={it.label} className="bg-white px-4 py-3">
            <p className={cn("text-2xl font-bold", it.value > 0 ? "text-amber-600" : "text-forest-dark")}>{it.value}</p>
            <p className="text-[11px] leading-tight text-muted-foreground">{it.label}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}
